import { useAppSelector } from "integrations/redux/hooks"
import {
    selectColorTheme,
    selectHideLegend,
    selectLegendPosition,
    selectPDFLegendScale,
    selectPDFLegendSize,
} from "./PDFSlice"
import { forwardRef } from "react"
import { GridData } from "./PDFAnnotations/PDFGridLines"
import { BorderData } from "./PDFAnnotations/PDFBorderTicks"
import { PDFLegend } from "./PDFLegend/PDFLegend"
import { PDFBottomLegend } from "./PDFLegend/PDFBottomLegend"
import classNames from "classnames"
import { useViewport } from "features/map/GlobalViewState"

type PDFAnnotationsType = {
    offsetX: number
    offsetY: number
    width: number
    height: number
    name: string
}

export const BORDER_THICKNESS = 12
export const BORDER_TICK_LENGTH = 0
export const DEFAULT_HEIGHT = 900
export const DEFAULT_WIDTH = 1300

export const PDFSnapshotContainer = forwardRef<HTMLDivElement, PDFAnnotationsType>(
    function Annotations(props, ref) {
        const { width, height, offsetX, offsetY } = props
        const pdfLegendSize = useAppSelector(selectPDFLegendSize())
        const pdfLegendScale = useAppSelector(selectPDFLegendScale())
        const legendPosition = useAppSelector(selectLegendPosition())
        const theme = useAppSelector(selectColorTheme())
        const view = useViewport()
        const hideLegend = useAppSelector(selectHideLegend())

        let innerWidth = width
        let innerHeight = height
        if (!hideLegend) {
            if (legendPosition === "right")
                innerWidth = width - (pdfLegendSize * height) / DEFAULT_HEIGHT
            if (legendPosition === "bottom")
                innerHeight = height - (pdfLegendSize * width) / DEFAULT_WIDTH + 15
        }

        return (
            <div
                className={classNames("absolute pointer-events-none overflow-hidden", {
                    dark: theme === "dark",
                })}
                style={{ top: offsetY, left: offsetX, width, height }}
                id="pdf-snapshot"
            >
                <div className="w-full h-full relative" id="annotations-ref-full">
                    <BorderData
                        width={innerWidth}
                        height={innerHeight}
                        view={view}
                        offsetX={offsetX}
                        offsetY={offsetY}
                    />
                    <div
                        className="relative h-full border-white dark:border-black-900"
                        id="annotation-border-element"
                        style={{
                            borderWidth: hideLegend ? 0 : BORDER_THICKNESS * pdfLegendScale,
                            width: innerWidth,
                        }}
                    >
                        <div className="w-full h-full border-[#b0b0b0] dark:border-black-900">
                            <GridData
                                width={innerWidth}
                                height={innerHeight}
                                view={view}
                                offsetX={offsetX}
                                offsetY={offsetY}
                            />
                        </div>
                    </div>
                    {!hideLegend && (
                        <div ref={ref} className="w-full h-full absolute top-0 left-0">
                            {legendPosition === "right" && <PDFLegend {...props} />}
                            {legendPosition === "bottom" && <PDFBottomLegend {...props} />}
                        </div>
                    )}
                </div>
            </div>
        )
    }
)
